import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createApiUrl } from '../../config/api';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Tooltip,
  Dialog,
  Badge,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Assignment as TaskIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
  Flag as PriorityIcon,
  Star as RatingIcon
} from '@mui/icons-material';
import TaskForm from './TaskForm';
import TaskDetail from './TaskDetail';

const TaskList = ({ onNavigateToTask, selectedTaskId, onBack }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Task endpoints are auth-scoped per role (TKT-221 Layer B); always send the token.
  const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token')}` });

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [assignedUserFilter, setAssignedUserFilter] = useState('');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(12);

  // Form states
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [viewingTask, setViewingTask] = useState(null);

  // Metadata for filters
  const [taskTypes, setTaskTypes] = useState([]);
  const [taskPriorities, setTaskPriorities] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [users, setUsers] = useState([]);

  // Fetch metadata for filters
  const fetchMetadata = async () => {
    try {
      const [typesRes, prioritiesRes, statusesRes, usersRes] = await Promise.all([
        fetch(createApiUrl('api/tasks/lookup/types')),
        fetch(createApiUrl('api/tasks/lookup/priorities')),
        fetch(createApiUrl('api/tasks/lookup/statuses')),
        fetch(createApiUrl('api/users'), {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }).catch(() => ({ ok: false, json: () => ({ users: [] }) }))
      ]);

      const [typesData, prioritiesData, statusesData, usersData] = await Promise.all([
        typesRes.json(),
        prioritiesRes.json(),
        statusesRes.json(),
        usersRes.json()
      ]);

      if (typesRes.ok) setTaskTypes(typesData.data || []);
      if (prioritiesRes.ok) setTaskPriorities(prioritiesData.data || []);
      if (statusesRes.ok) setTaskStatuses(statusesData.data || []);
      if (usersRes.ok) setUsers(usersData.users || []);
    } catch (error) {
      console.error('Failed to fetch metadata:', error);
    }
  };

  // Fetch tasks
  // Note: By default, this shows ALL tasks (not filtered by logged-in user)
  // Users can filter by assigned_user_id using the "Assigned To" filter dropdown
  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status_id', statusFilter);
      if (priorityFilter) params.append('priority_id', priorityFilter);
      if (typeFilter) params.append('type_id', typeFilter);
      if (assignedUserFilter) params.append('assigned_user_id', assignedUserFilter);

      const response = await fetch(createApiUrl(`api/tasks?${params.toString()}`), { headers: authHeaders() });
      const data = await response.json();

      if (response.ok) {
        setTasks(data.data || []);
        setTotalPages(data.pagination?.pages || 1);
        setTotal(data.pagination?.total || 0);
      } else {
        setError(data.message || 'Failed to fetch tasks');
      }
    } catch (error) {
      setError('Network error occurred');
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchMetadata();
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [page, searchTerm, statusFilter, priorityFilter, typeFilter, assignedUserFilter]);

  // Auto-open task detail when selectedTaskId is provided (from notifications)
  useEffect(() => {
    if (selectedTaskId && !viewingTask && !editingTask && !showCreateForm) {
      // Check the mode from sessionStorage
      const mode = sessionStorage.getItem('taskViewMode') || 'view';
      sessionStorage.removeItem('taskViewMode'); // Clean up after use
      
      // Find the task in current loaded tasks first
      const task = tasks.find(t => t.id === parseInt(selectedTaskId));
      
      const openTask = (taskData) => {
        if (mode === 'edit') {
          setEditingTask(taskData);
          setShowCreateForm(true);
        } else {
          setViewingTask(taskData);
        }
      };
      
      if (task) {
        openTask(task);
      } else {
        // If not in current list, fetch the specific task
        const fetchSpecificTask = async () => {
          try {
            const response = await fetch(createApiUrl(`api/tasks/${selectedTaskId}`), { headers: authHeaders() });
            const data = await response.json();
            if (response.ok && data.data) {
              openTask(data.data);
            }
          } catch (error) {
            console.error('Error fetching specific task:', error);
          }
        };
        fetchSpecificTask();
      }
    }
  }, [selectedTaskId, tasks, viewingTask, editingTask, showCreateForm]);

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'status':
        setStatusFilter(value);
        break;
      case 'priority':
        setPriorityFilter(value);
        break;
      case 'type':
        setTypeFilter(value);
        break;
      case 'assignedUser':
        setAssignedUserFilter(value);
        break;
    }
    setPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setPriorityFilter('');
    setTypeFilter('');
    setAssignedUserFilter('');
    setPage(1);
  };

  const handleCreateTask = () => {
    setEditingTask(null);
    setShowCreateForm(true);
  };

  const handleEditTask = async (task) => {
    try {
      // Fetch full task details for editing
      const response = await fetch(createApiUrl(`api/tasks/${task.id}`), { headers: authHeaders() });
      const data = await response.json();
      
      if (response.ok) {
        setEditingTask(data.data);
        setShowCreateForm(true);
      } else {
        setError(data.message || 'Failed to fetch task details');
      }
    } catch (error) {
      setError('Failed to fetch task details');
      console.error('Error fetching task details:', error);
    }
  };

  const handleViewTask = (task) => {
    setViewingTask(task);
  };

  const handleTaskSubmit = (taskData) => {
    setShowCreateForm(false);
    const wasEditing = editingTask;
    setEditingTask(null);
    fetchTasks(); // Refresh the list
    
    // If we came from notifications (selectedTaskId exists) and were editing, go back to main page
    if (selectedTaskId && wasEditing && onBack) {
      onBack();
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      const response = await fetch(createApiUrl(`api/tasks/${taskId}`), {
        method: 'DELETE',
        headers: authHeaders()
      });

      if (response.ok) {
        fetchTasks(); // Refresh the list
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to delete task');
      }
    } catch (error) {
      setError('Network error occurred');
      console.error('Error deleting task:', error);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical': return '#d32f2f';
      case 'urgent': return '#f57c00';
      case 'high': return '#ff9800';
      case 'normal': case 'medium': return '#2196f3';
      case 'low': return '#4caf50';
      default: return '#9e9e9e';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return '#4caf50';
      case 'in progress': return '#2196f3';
      case 'not started': return '#9e9e9e';
      case 'on hold': return '#ff9800';
      case 'cancelled': return '#f44336';
      case 'overdue': return '#d32f2f';
      default: return '#9e9e9e';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const TaskCard = ({ task }) => (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6" component="h3" sx={{ 
            fontWeight: 'bold', 
            fontSize: '1.1rem',
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}>
            {task.subject}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, ml: 1 }}>
            <Chip
              size="small"
              label={task.priority_name}
              sx={{ 
                bgcolor: getPriorityColor(task.priority_name),
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.75rem'
              }}
            />
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Chip
            size="small"
            label={task.status_name}
            sx={{ 
              bgcolor: getStatusColor(task.status_name),
              color: 'white',
              mb: 1
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <TaskIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
            {task.type_name}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
            {task.customer_company_name ? (
              <>
                <BusinessIcon sx={{ fontSize: 16, mr: 0.5 }} />
                {task.customer_company_name}
              </>
            ) : (
              <>
                <PersonIcon sx={{ fontSize: 16, mr: 0.5 }} />
                {task.customer_first_name} {task.customer_last_name}
              </>
            )}
          </Typography>
          
          {task.assigned_user_name && (
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Assigned: {task.assigned_user_name}
            </Typography>
          )}
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <ScheduleIcon sx={{ fontSize: 16, mr: 0.5 }} />
            {formatDate(task.start_time)}
          </Typography>
          
          {task.rating_name && (
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
              <RatingIcon sx={{ fontSize: 16, mr: 0.5 }} />
              {task.rating_name}
            </Typography>
          )}
        </Box>

        {task.notes && (
          <Typography variant="body2" color="text.secondary" sx={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            fontStyle: 'italic'
          }}>
            {task.notes}
          </Typography>
        )}
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0, justifyContent: 'space-between' }}>
        <Box>
          <Tooltip title={t('tasks.viewDetails')}>
            <IconButton 
              size="small" 
              onClick={() => handleViewTask(task)}
              sx={{ color: '#2196f3' }}
            >
              <ViewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('tasks.editTask')}>
            <IconButton 
              size="small" 
              onClick={() => handleEditTask(task)}
              sx={{ color: '#ff9800' }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('tasks.deleteTask')}>
            <IconButton 
              size="small" 
              onClick={() => handleDeleteTask(task.id)}
              sx={{ color: '#f44336' }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="caption" color="text.secondary">
          ID: {task.id}
        </Typography>
      </CardActions>
    </Card>
  );

  if (loading && tasks.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box data-demo-target="page-tasks-root">
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#000062', fontSize: { xs: '1.5rem', md: '2.125rem' }, mb: 3 }}>
            Task Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total: {total} tasks
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateTask}
          sx={{ bgcolor: '#1976d2' }}
        >
          Create Task
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
            }}
            sx={{ minWidth: 250 }}
            size="small"
          />

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t('tasks.status')}</InputLabel>
            <Select
              value={statusFilter}
              label={t('tasks.status')}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {taskStatuses.map((status) => (
                <MenuItem key={status.id} value={status.id}>
                  {status.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t('tasks.priority')}</InputLabel>
            <Select
              value={priorityFilter}
              label={t('tasks.priority')}
              onChange={(e) => handleFilterChange('priority', e.target.value)}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {taskPriorities.map((priority) => (
                <MenuItem key={priority.id} value={priority.id}>
                  {priority.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t('tasks.type')}</InputLabel>
            <Select
              value={typeFilter}
              label={t('tasks.type')}
              onChange={(e) => handleFilterChange('type', e.target.value)}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {taskTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('tasks.assignedTo')}</InputLabel>
            <Select
              value={assignedUserFilter}
              label={t('tasks.assignedTo')}
              onChange={(e) => handleFilterChange('assignedUser', e.target.value)}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.first_name} {user.last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button 
            variant="outlined" 
            onClick={clearFilters}
            startIcon={<FilterIcon />}
          >
            Clear
          </Button>
        </Box>
      </Paper>

      {/* Task Grid */}
      {tasks.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <TaskIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No tasks found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {searchTerm || statusFilter || priorityFilter || typeFilter || assignedUserFilter
              ? 'Try adjusting your filters'
              : 'Create your first task to get started'
            }
          </Typography>
          {!searchTerm && !statusFilter && !priorityFilter && !typeFilter && !assignedUserFilter && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateTask}
              sx={{ bgcolor: '#1976d2' }}
            >
              Create First Task
            </Button>
          )}
        </Paper>
      ) : (
        <>
          <Grid container spacing={3}>
            {tasks.map((task) => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={task.id}>
                <TaskCard task={task} />
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <Typography sx={{ mx: 2, alignSelf: 'center' }}>
                Page {page} of {totalPages}
              </Typography>
              <Button
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Create/Edit Task Dialog */}
      <Dialog
        open={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <TaskForm
          task={editingTask}
          onSubmit={handleTaskSubmit}
          onCancel={() => {
            setShowCreateForm(false);
            // If we came from notifications (selectedTaskId exists), go back to main page
            if (selectedTaskId && editingTask && onBack) {
              onBack();
            }
          }}
        />
      </Dialog>

      {/* View Task Dialog */}
      <Dialog
        open={!!viewingTask}
        onClose={() => setViewingTask(null)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        {viewingTask && (
          <TaskDetail
            taskId={viewingTask.id}
            onClose={() => {
              setViewingTask(null);
              // If we came from notifications (selectedTaskId exists), go back to main page
              if (selectedTaskId && onBack) {
                onBack();
              }
            }}
            onEdit={() => {
              setEditingTask(viewingTask);
              setViewingTask(null);
              setShowCreateForm(true);
            }}
            onTaskUpdate={(updatedTask) => {
              // Refresh the task list after marking as complete
              fetchTasks();
              // Update the viewing task if it's the same one
              if (viewingTask && viewingTask.id === updatedTask.id) {
                setViewingTask(updatedTask);
              }
            }}
          />
        )}
      </Dialog>
    </Box>
  );
};

export default TaskList;
